import BaseApi from './BaseApi.js';
import { formatMaintenanceDate } from './formatMaintenanceDate.js';

class ResourcesApi extends BaseApi {
	static get endpoint() {
		return this.buildOrgEndpoint('/resource');
	}

	static async getResources() {
		return await this.get(this.endpoint);
	}

	static async getResourceById(resourceId) {
		const endpoint = `${this.endpoint}/${resourceId}`;
		return await this.get(endpoint);
	}

	static async addResource(resourceData) {
		return await this.post(this.endpoint, resourceData);
	}

	static async updateResource(resourceId, resourceData) {
		const endpoint = `${this.endpoint}/${resourceId}`;
		return await this.put(endpoint, resourceData);
	}

	static async deleteResource(resourceId) {
		const endpoint = `${this.endpoint}/${resourceId}`;
		return await this.delete(endpoint);
	}

	// Toggle resource publication status
	static async togglePublication(resourceId) {
		const endpoint = `${this.endpoint}/${resourceId}/toggle-publication`;
		return await this.apiRequest(endpoint, { method: 'PATCH' });
	}

	// Get organization resource types
	static async getResourceTypes() {
		const endpoint = this.buildOrgEndpoint('/resource-types');
		return await this.get(endpoint);
	}

	// Update resource type
	static async updateResourceType(typeId, typeData) {
		const endpoint = `${this.buildOrgEndpoint('/resource-type')}/${typeId}`;
		return await this.put(endpoint, typeData);
	}

	// Get incomplete events for resource
	static async getIncompleteEvents(resourceId) {
		const endpoint = `${this.endpoint}/${resourceId}/incomplete-events`;
		return await this.get(endpoint);
	}

	// Get resource counters
	static async getResourceCounters(resourceId) {
		const endpoint = `${this.endpoint}/${resourceId}/counters`;
		return await this.get(endpoint);
	}

	// Get instructor information and schedules
	static async getInstructorInfo(resourceId) {
		const endpoint = `${this.endpoint}/${resourceId}/instructor-info`;
		return await this.get(endpoint);
	}

	// Block resource type (prevent scheduling together)
	static async blockResourceType(typeId, blockedTypeId) {
		const endpoint = `${this.buildOrgEndpoint('/resource-type')}/${typeId}/block/${blockedTypeId}`;
		return await this.post(endpoint, {});
	}

	// Unblock resource type
	static async unblockResourceType(typeId, blockedTypeId) {
		const endpoint = `${this.buildOrgEndpoint('/resource-type')}/${typeId}/unblock/${blockedTypeId}`;
		return await this.delete(endpoint);
	}

	// ============ Dropdown Data API ============

	// Get locations for dropdown
	static async getLocations() {
		const endpoint = this.buildOrgEndpoint('/location');
		return await this.get(endpoint);
	}

	// Get resources dropdown data (includes pilotRatings, instructorRatings, medicalClass)
	static async getResourcesDropdownData() {
		const endpoint = this.buildOrgEndpoint('/resource');
		return await this.get(endpoint);
	}

	// ============ Aircraft API ============

	// Get full aircraft data for View Aircraft Modal
	static async getAircraft(aircraftId, orgUnique) {
		const base = orgUnique ? `/org/${orgUnique}` : this.buildOrgEndpoint('');
		return await this.get(`${base}/aircraft/${aircraftId}`);
	}

	// Update aircraft data
	static async updateAircraft(aircraftId, data) {
		const endpoint = this.buildOrgEndpoint(`/aircraft/${aircraftId}`);
		return await this.put(endpoint, data);
	}

	// Delete aircraft
	// DELETE /api/v1/org/{org}/aircraft/{aircraftId}
	static async deleteAircraft(aircraftId) {
		const endpoint = this.buildOrgEndpoint(`/aircraft/${aircraftId}`);
		return await this.delete(endpoint);
	}

	// ============ Simulator API ============

	// Get full simulator data for View Simulator Modal
	static async getSimulator(simulatorId, orgUnique) {
		const base = orgUnique ? `/org/${orgUnique}` : this.buildOrgEndpoint('');
		return await this.get(`${base}/simulator/${simulatorId}`);
	}

	// ============ Inspections API (Symfony) ============

	// Get inspections for aircraft
	static async getInspections(aircraftId, timezone, orgUnique) {
		const tz = timezone || this.getTimezoneId();
		const base = orgUnique ? `/org/${orgUnique}` : this.buildOrgEndpoint('');
		return await this.get(`${base}/aircraft/${aircraftId}/inspections?timezone=${encodeURIComponent(tz)}`);
	}

	// Add inspection to aircraft
	static async addInspection(aircraftId, inspectionData) {
		const endpoint = this.buildOrgEndpoint(`/aircraft/${aircraftId}/inspections`);
		return await this.post(endpoint, inspectionData);
	}

	// Renew/update inspection
	static async renewInspection(inspectionId, inspectionData) {
		const endpoint = this.buildOrgEndpoint(`/inspection/${inspectionId}`);
		return await this.put(endpoint, inspectionData);
	}

	// Delete inspection
	static async deleteInspection(inspectionId) {
		const endpoint = this.buildOrgEndpoint(`/inspection/${inspectionId}`);
		return await this.delete(endpoint);
	}

	// ============ Counters/Meters API (Symfony) ============

	// Get counters history for aircraft
	// Symfony endpoint: POST /api/v1/org/{org}/aircraft/{aircraftId}/counters/history
	static async getAircraftCountersHistory(aircraftId, options = {}) {
		const endpoint = this.buildOrgEndpoint(`/aircraft/${aircraftId}/counters/history`);
		return await this.post(endpoint, {
			position: options.position || 0,
			startTime: options.startTime || null,
			finishTime: options.finishTime || null,
			timeVariable: options.timeVariable || 'start',
			pilotIds: options.pilotIds || [],
			sicPilotIds: options.sicPilotIds || [],
			sortValue: options.sortValue || 'eventStartTime',
			sortType: options.sortType || 'DESC'
		});
	}

	// Get counters history for simulator
	// Symfony endpoint: POST /api/v1/org/{org}/simulator/{simulatorId}/counters/history
	static async getSimulatorCountersHistory(simulatorId, options = {}) {
		const endpoint = this.buildOrgEndpoint(`/simulator/${simulatorId}/counters/history`);
		return await this.post(endpoint, {
			position: options.position || 0,
			startTime: options.startTime || null,
			finishTime: options.finishTime || null,
			timeVariable: options.timeVariable || 'start',
			pilotIds: options.pilotIds || [],
			sicPilotIds: options.sicPilotIds || [],
			sortValue: options.sortValue || 'eventStartTime',
			sortType: options.sortType || 'DESC'
		});
	}

	// Get counters history for other resources (instructors, etc.)
	// Symfony endpoint: POST /api/v1/org/{org}/resources/counters-history
	static async getResourcesCountersHistory(resourceIds, options = {}) {
		const endpoint = this.buildOrgEndpoint('/resources/counters-history');
		return await this.post(endpoint, {
			resourceIds: Array.isArray(resourceIds) ? resourceIds : [resourceIds],
			page: options.page || 0
		});
	}

	// ============ Weight & Balance API (Symfony) ============

	// Save weight and balance settings
	static async saveWeightAndBalance(aircraftId, data) {
		const endpoint = this.buildOrgEndpoint(`/aircraft/${aircraftId}/weight-and-balance`);
		return await this.put(endpoint, data);
	}

	// ============ Envelope API (Symfony) ============

	// Search aircraft models for envelope templates (from global models database)
	static async searchAircraftModels(query, page = 1) {
		const endpoint = this.buildOrgEndpoint('/envelope/search-models');
		return await this.post(endpoint, { query, page, limit: 10 });
	}

	// Get CG envelope for aircraft
	static async getEnvelope(aircraftId, orgUniqueOverride = null) {
		const base = orgUniqueOverride ? `/org/${orgUniqueOverride}` : this.buildOrgEndpoint('');
		return await this.get(`${base}/aircraft/${aircraftId}/envelope`);
	}

	// Save CG envelope for aircraft
	static async saveEnvelope(aircraftId, data) {
		const endpoint = this.buildOrgEndpoint(`/aircraft/${aircraftId}/envelope`);
		return await this.put(endpoint, data);
	}

	// ============ Fuel Tank API (Symfony) ============

	// Get fuel tank data for aircraft
	static async getFuelTank(aircraftId) {
		const endpoint = this.buildOrgEndpoint(`/aircraft/${aircraftId}/fuel-tank`);
		return await this.get(endpoint);
	}

	// Get all available fuel tank models (for sliding/dynamic tank selection)
	static async getAllFuelTanks() {
		const endpoint = this.buildOrgEndpoint('/fuel-tanks');
		return await this.get(endpoint);
	}

	// Save fuel tank graph data
	static async saveFuelTankGraph(aircraftId, data) {
		const endpoint = this.buildOrgEndpoint(`/aircraft/${aircraftId}/fuel-tank-graph`);
		return await this.put(endpoint, data);
	}

	// ============ Maintenance API ============

	// Get timezone ID for maintenance API
	static getTimezoneId() {
		// Try to get from globals (set by AngularJS)
		if (window.globals?.timeZone?.id) return window.globals.timeZone.id;
		if (window.globals?._timezone) return window.globals._timezone;
		// Try to get from serverData
		if (window.serverData?.timeZoneId) return window.serverData.timeZoneId;
		if (window.serverData?._timezone) return window.serverData._timezone;
		// Fallback to browser timezone (IANA format like "America/New_York")
		return Intl.DateTimeFormat().resolvedOptions().timeZone;
	}

	// Get maintenance events for a resource
	static async getMaintenance(resourceId) {
		const endpoint = `${this.endpoint}/${resourceId}/maintenance`;
		return await this.get(endpoint);
	}

	// Add maintenance event
	static async addMaintenance(resourceId, maintenanceData, orgUniqueOverride = null) {
		const base = orgUniqueOverride ? `/org/${orgUniqueOverride}/resource` : this.endpoint;
		const endpoint = `${base}/${resourceId}/maintenance`;
		const timezone = maintenanceData.timezoneId || this.getTimezoneId();
		return await this.post(endpoint, {
			startTime: formatMaintenanceDate(maintenanceData.startTime, timezone),
			finishTime: formatMaintenanceDate(maintenanceData.finishTime, timezone),
			comments: maintenanceData.comments || '',
			timezone: timezone
		});
	}

	// Update maintenance event
	static async updateMaintenance(resourceId, maintenanceId, maintenanceData, orgUniqueOverride = null) {
		const base = orgUniqueOverride ? `/org/${orgUniqueOverride}/resource` : this.endpoint;
		const endpoint = `${base}/${resourceId}/maintenance/${maintenanceId}`;
		const timezone = maintenanceData.timezoneId || this.getTimezoneId();
		return await this.patch(endpoint, {
			startTime: formatMaintenanceDate(maintenanceData.startTime, timezone),
			finishTime: formatMaintenanceDate(maintenanceData.finishTime, timezone),
			comments: maintenanceData.comments || '',
			timezone: timezone
		});
	}

	// Delete maintenance event
	static async deleteMaintenance(resourceId, maintenanceId, orgUniqueOverride = null) {
		const base = orgUniqueOverride ? `/org/${orgUniqueOverride}/resource` : this.endpoint;
		const endpoint = `${base}/${resourceId}/maintenance/${maintenanceId}`;
		return await this.delete(endpoint);
	}

	// Move maintenance event (drag-and-drop)
	static async moveMaintenance(resourceId, maintenanceId, maintenanceData, orgUniqueOverride = null) {
		const base = orgUniqueOverride ? `/org/${orgUniqueOverride}/resource` : this.endpoint;
		const endpoint = `${base}/${resourceId}/maintenance/${maintenanceId}/move`;
		const timezone = maintenanceData.timezoneId || this.getTimezoneId();
		return await this.patch(endpoint, {
			startTime: formatMaintenanceDate(maintenanceData.startTime, timezone),
			finishTime: formatMaintenanceDate(maintenanceData.finishTime, timezone),
			comments: maintenanceData.comments || '',
			timezone: timezone
		});
	}

	// ============ Access/Permissions API ============

	// Get resource access settings
	static async getAccess(resourceId) {
		const endpoint = `${this.endpoint}/${resourceId}/access`;
		return await this.get(endpoint);
	}

	// Update resource accessibility (for all users)
	static async updateAccess(resourceId, accessibility) {
		const endpoint = `${this.endpoint}/${resourceId}/access`;
		return await this.put(endpoint, { accessibility });
	}

	// Add user-specific access setting
	static async addUserAccess(resourceId, userId, accessibility) {
		const endpoint = `${this.endpoint}/${resourceId}/access/user`;
		return await this.post(endpoint, { userId: parseInt(userId, 10), accessibility });
	}

	// Update user-specific access setting
	static async updateUserAccess(resourceId, userId, accessibility) {
		const endpoint = `${this.endpoint}/${resourceId}/access/user/${parseInt(userId, 10)}`;
		return await this.put(endpoint, { accessibility });
	}

	// Delete user-specific access setting
	static async deleteUserAccess(resourceId, userId) {
		const endpoint = `${this.endpoint}/${resourceId}/access/user/${parseInt(userId, 10)}`;
		return await this.delete(endpoint);
	}

	// ============ Resource Image API ============

	// Upload resource image (for W&B Envelope Image)
	// Symfony endpoint: POST /api/v1/org/{org}/resource/{resourceId}/image
	static async uploadResourceImage(resourceId, file) {
		const formData = new FormData();
		formData.append('file', file);
		const endpoint = this.buildOrgEndpoint(`/resource/${resourceId}/image`);
		// apiRequest auto-detects FormData and skips Content-Type header
		return await this.apiRequest(endpoint, {
			method: 'POST',
			body: formData
		});
	}

	// Delete resource image
	// Symfony endpoint: DELETE /api/v1/org/{org}/resource/{resourceId}/image
	static async deleteResourceImage(resourceId) {
		const endpoint = this.buildOrgEndpoint(`/resource/${resourceId}/image`);
		return await this.delete(endpoint);
	}

	// ============ Organization Users API ============

	// Get organization users list (with names, for selects/permissions)
	static async getOrganizationUsersList() {
		const endpoint = this.buildOrgEndpoint('/users-list');
		return await this.get(endpoint);
	}

	// Get organization users with extended data (locations, statements, for dashboard)
	static async getOrganizationUsers() {
		const endpoint = this.buildOrgEndpoint('/users');
		return await this.get(endpoint);
	}
}

export default ResourcesApi;
